import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, FileText, Briefcase } from "lucide-react";
import { AttivitaPanel } from "@/components/AttivitaPanel";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useCrm } from "@/lib/crm";
import { STAGES, isAcq } from "@/lib/constants";

const empty = {
  nome: "", disciplina: "", atleti: 0, collaboratore_id: "", stage: "ricerca",
  ricavo: 0, dimensione: "media", citta: "", referente: "", telefono: "", email: "",
  rapporto_famiglie: "", focus_crescita: false, eventi_interni: false, note: "",
  compenso_tipo: "default", compenso_valore: 0, lavori: [], listino: [],
  tipo_progetto: "", partecipanti: 0, data_evento: "", location: "", ente_committente: "", ricavo_previsto: 0, sezione_stampa: "interna", progetto_id: "",
  canale: "privati", partner_id: "", accordo_tipo: "provvigione", accordo_valore: 0,
};

export const PARTNER_TIPOLOGIE = ["Parrucchiere", "Beauty farm", "Centro estetico", "Palestra", "Negozio", "Altro"];

export function SsdDialog({ open, onOpenChange, onSave, collaboratori, initial, canale }) {
  const { isTitolare } = useAuth();
  const { settings, api, progetti, ssd, refresh } = useCrm();
  const isRitr = settings?.settore === "ritrattistica";
  const isPartner = isRitr && canale === "partner";
  const partners = (ssd || []).filter((x) => x.canale === "partner");
  const stagesSrc = isPartner ? settings?.stages_partner : settings?.stages;
  const stagesList = stagesSrc?.length ? stagesSrc : STAGES;
  const [form, setForm] = useState(empty);
  const [compVal, setCompVal] = useState("");

  const compValFor = (tipo, v) => {
    if (tipo === "percentuale") return v ? String(Math.round((Number(v) || 0) * 100)) : "";
    if (tipo === "fisso") return v ? String(v) : "";
    return "";
  };

  useEffect(() => {
    if (open) {
      const base = initial ? { ...empty, ...initial } : { ...empty, stage: (stagesList[0] && stagesList[0].key) || "ricerca" };
      if (!base.listino || base.listino.length === 0) {
        base.listino = (settings?.listino || []).map((it) => ({ ...it }));
      }
      setForm(base);
      setCompVal(compValFor(base.compenso_tipo, base.compenso_valore));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const changeCompTipo = (tipo) => {
    setForm((f) => ({ ...f, compenso_tipo: tipo, compenso_valore: 0 }));
    setCompVal("");
  };

  const changeCompVal = (raw) => {
    setCompVal(raw);
    const n = raw === "" ? 0 : (Number(raw) || 0);
    setForm((f) => ({ ...f, compenso_valore: f.compenso_tipo === "percentuale" ? n / 100 : n }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lavoriTotal = (form.lavori || []).reduce((a, l) => a + (Number(l.importo) || 0), 0);
  const addLavoro = () => setForm((f) => ({ ...f, lavori: [...(f.lavori || []), { id: (crypto.randomUUID?.() || String(Date.now()) + Math.random()), data: "", descrizione: "", importo: 0 }] }));
  const setLavoro = (idx, k, v) => setForm((f) => ({ ...f, lavori: f.lavori.map((l, j) => (j === idx ? { ...l, [k]: v } : l)) }));
  const removeLavoro = (idx) => setForm((f) => ({ ...f, lavori: f.lavori.filter((_, j) => j !== idx) }));

  const addVoce = () => setForm((f) => ({ ...f, listino: [...(f.listino || []), { id: `n-${Date.now()}`, categoria: "Generale", nome: "Nuova voce", prezzo: 0 }] }));
  const setVoce = (idx, k, v) => setForm((f) => ({ ...f, listino: f.listino.map((x, j) => (j === idx ? { ...x, [k]: v } : x)) }));
  const removeVoce = (idx) => setForm((f) => ({ ...f, listino: f.listino.filter((_, j) => j !== idx) }));
  const resetListino = () => setForm((f) => ({ ...f, listino: (settings?.listino || []).map((x) => ({ ...x })) }));

  const submit = () => {
    if (!form.nome.trim()) return;
    const lavori = (form.lavori || []).map((l) => ({ ...l, importo: Number(l.importo) || 0 }));
    onSave({
      ...form,
      atleti: Number(form.atleti) || 0,
      lavori,
      listino: (form.listino || []).map((x) => ({ ...x, prezzo: Number(x.prezzo) || 0 })),
      ricavo: lavori.length ? lavori.reduce((a, l) => a + l.importo, 0) : (Number(form.ricavo) || 0),
      compenso_tipo: form.compenso_tipo || "default",
      compenso_valore: Number(form.compenso_valore) || 0,
      collaboratore_id: form.collaboratore_id || null,
      ...(isRitr ? { canale: isPartner ? "partner" : "privati", partner_id: isPartner ? "" : (form.partner_id || "") } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ssd-dialog">
        <DialogHeader>
          <DialogTitle>{isPartner ? (initial ? "Modifica partner" : "Nuovo partner") : initial ? (isRitr ? "Modifica cliente" : "Modifica SSD") : (isRitr ? "Nuovo cliente" : "Nuova SSD")}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5">
            <Label>{isPartner ? "Nome attività partner *" : isRitr ? "Nome cliente *" : "Nome società *"}</Label>
            <Input data-testid="ssd-nome-input" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder={isPartner ? "es. Salone Bellezza Rossi" : isRitr ? "es. Mario Rossi / Famiglia Bianchi" : "es. Basket Virtus"} />
          </div>

          {isPartner ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-1.5">
                <Label>Tipologia</Label>
                <Select value={form.disciplina || ""} onValueChange={(v) => set("disciplina", v)}>
                  <SelectTrigger data-testid="ssd-partner-tipologia"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{PARTNER_TIPOLOGIE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Accordo</Label>
                <Select value={form.accordo_tipo || "provvigione"} onValueChange={(v) => set("accordo_tipo", v)}>
                  <SelectTrigger data-testid="ssd-accordo-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="provvigione">Provvigione al partner</SelectItem>
                    <SelectItem value="sconto">Sconto ai suoi clienti</SelectItem>
                    <SelectItem value="scambio">Scambio / visibilità</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Valore (%)</Label>
                <Input data-testid="ssd-accordo-valore" type="number" value={form.accordo_valore || 0} onChange={(e) => set("accordo_valore", Number(e.target.value) || 0)} />
              </div>
            </div>
          ) : isRitr ? (
            <>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Portrait Experience</Label>
                <Select value={form.progetto_id || "none"} onValueChange={(v) => set("progetto_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="ssd-progetto-select"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna (servizio singolo)</SelectItem>
                    {(progetti || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Portato dal partner</Label>
                <Select value={form.partner_id || "none"} onValueChange={(v) => set("partner_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="ssd-partner-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno (contatto diretto)</SelectItem>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Tipo di servizio</Label>
              <Select value={form.tipo_progetto || ""} onValueChange={(v) => set("tipo_progetto", v)}>
                <SelectTrigger data-testid="ssd-tipo-progetto-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {(settings?.tipi_progetto || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Disciplina</Label>
                <Input value={form.disciplina} onChange={(e) => set("disciplina", e.target.value)} placeholder="Calcio, Basket..." />
              </div>
              <div className="grid gap-1.5">
                <Label>N° atleti</Label>
                <Input type="number" value={form.atleti} onChange={(e) => set("atleti", e.target.value)} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Fase pipeline</Label>
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger data-testid="ssd-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stagesList.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Collaboratore</Label>
              <Select value={form.collaboratore_id || "none"} onValueChange={(v) => set("collaboratore_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="ssd-collaborator-select"><SelectValue placeholder="Assegna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {collaboratori.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isPartner && (<>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{lavoriTotal > 0 ? "Ricavo (somma lavori)" : "Ricavo contratto (€)"}</Label>
              <Input data-testid="ssd-revenue-input" type="number" value={lavoriTotal > 0 ? lavoriTotal : form.ricavo} disabled={lavoriTotal > 0} onChange={(e) => set("ricavo", e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              {isRitr ? (
                <>
                  <Label>Sezione stampa</Label>
                  <Select value={form.sezione_stampa || "interna"} onValueChange={(v) => set("sezione_stampa", v)}>
                    <SelectTrigger data-testid="ssd-sezione-stampa"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interna">Interna</SelectItem>
                      <SelectItem value="esterna">Esterna (outsourcing)</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <>
                  <Label>Dimensione</Label>
                  <Select value={form.dimensione || "media"} onValueChange={(v) => set("dimensione", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="piccola">Piccola</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="grande">Grande</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          </div>

          {isRitr && !form.progetto_id && (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Data shooting</Label><Input data-testid="ssd-data-shooting" type="date" value={form.data_evento || ""} onChange={(e) => set("data_evento", e.target.value)} /></div>
              <div className="grid gap-1.5"><Label>Location</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Lavori / Ricavi nel tempo</Label>
              <Button type="button" data-testid="ssd-add-lavoro-button" variant="outline" size="sm" className="h-7 rounded-full gap-1" onClick={addLavoro}><Plus className="h-3.5 w-3.5" /> Aggiungi</Button>
            </div>
            {(!form.lavori || form.lavori.length === 0) ? (
              <p className="text-[11px] text-slate-400">Nessun lavoro. Aggiungi i singoli servizi/incassi (con data): il ricavo totale si calcola in automatico e ogni lavoro matura sulla rata giusta.</p>
            ) : (
              <div className="space-y-2">
                {form.lavori.map((l, idx) => (
                  <div key={l.id} data-testid={`ssd-lavoro-${idx}`} className="grid grid-cols-[9rem_1fr_5.5rem_auto] gap-2 items-center">
                    <Input type="date" value={l.data || ""} onChange={(e) => setLavoro(idx, "data", e.target.value)} className="h-8 text-xs" data-testid={`ssd-lavoro-data-${idx}`} />
                    <Input value={l.descrizione || ""} onChange={(e) => setLavoro(idx, "descrizione", e.target.value)} placeholder="Descrizione" className="h-8 text-xs" data-testid={`ssd-lavoro-desc-${idx}`} />
                    <Input type="number" value={l.importo} onChange={(e) => setLavoro(idx, "importo", e.target.value)} placeholder="€" className="h-8 text-xs" data-testid={`ssd-lavoro-importo-${idx}`} />
                    <button type="button" onClick={() => removeLavoro(idx)} data-testid={`ssd-lavoro-remove-${idx}`} className="text-slate-300 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Listino dedicato</Label>
              <div className="flex items-center gap-1.5">
                {initial?.id && (form.listino || []).length > 0 && (
                  <Button type="button" data-testid="ssd-preventivo-button" variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary hover:text-primary" onClick={() => api.exportPreventivo(initial.id, form.nome).then(() => toast.success("Preventivo PDF generato")).catch(() => toast.error("Errore nel preventivo"))}><FileText className="h-3.5 w-3.5" /> Preventivo PDF</Button>
                )}
                {isTitolare && (
                  <>
                    <Button type="button" data-testid="ssd-listino-reset" variant="ghost" size="sm" className="h-7 text-xs text-slate-400 hover:text-primary" onClick={resetListino}>Da riferimento</Button>
                    <Button type="button" data-testid="ssd-listino-add" variant="outline" size="sm" className="h-7 rounded-full gap-1" onClick={addVoce}><Plus className="h-3.5 w-3.5" /> Voce</Button>
                  </>
                )}
              </div>
            </div>
            {(!form.listino || form.listino.length === 0) ? (
              <p className="text-[11px] text-slate-400">Nessuna voce. {isTitolare ? "Aggiungi voci o copiale dal listino di riferimento." : "Listino non impostato."}</p>
            ) : (
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {form.listino.map((it, idx) => (
                  <div key={it.id || idx} data-testid={`ssd-listino-item-${idx}`} className="grid grid-cols-[1fr_5.5rem_auto] gap-2 items-center">
                    <Input value={it.nome || ""} disabled={!isTitolare} onChange={(e) => setVoce(idx, "nome", e.target.value)} placeholder="Voce" className="h-8 text-xs" data-testid={`ssd-listino-nome-${idx}`} />
                    <div className="relative">
                      <Input type="number" value={it.prezzo} disabled={!isTitolare} onChange={(e) => setVoce(idx, "prezzo", e.target.value)} className="h-8 text-xs pr-6 text-right tabular-nums" data-testid={`ssd-listino-prezzo-${idx}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-[11px]">€</span>
                    </div>
                    {isTitolare ? (
                      <button type="button" onClick={() => removeVoce(idx)} data-testid={`ssd-listino-remove-${idx}`} className="text-slate-300 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    ) : <span className="w-4" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3" style={{ display: isTitolare ? undefined : "none" }}>
            <div className="grid gap-1.5">
              <Label>Compenso collaboratore</Label>
              <Select value={form.compenso_tipo || "default"} onValueChange={changeCompTipo}>
                <SelectTrigger data-testid="ssd-compenso-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">{isRitr ? "% del canale" : "% del collaboratore"}</SelectItem>
                  <SelectItem value="percentuale">% personalizzata</SelectItem>
                  <SelectItem value="fisso">Importo fisso €</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{form.compenso_tipo === "fisso" ? "Importo (€)" : form.compenso_tipo === "percentuale" ? "Percentuale (%)" : "—"}</Label>
              <Input
                data-testid="ssd-compenso-valore-input"
                type="number"
                disabled={!form.compenso_tipo || form.compenso_tipo === "default"}
                value={form.compenso_tipo === "default" ? "" : compVal}
                onChange={(e) => changeCompVal(e.target.value)}
                placeholder={form.compenso_tipo === "default" ? (isRitr ? "Usa % del canale" : "Usa % collaboratore") : ""}
              />
            </div>
          </div>
          </>)}

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Città</Label>
              <Input value={form.citta} onChange={(e) => set("citta", e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Referente</Label>
              <Input value={form.referente} onChange={(e) => set("referente", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Telefono</Label>
              <Input data-testid="ssd-telefono-input" value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input data-testid="ssd-email-input" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="per inviti calendario" />
            </div>
          </div>

          {!isRitr && (
            <>
              <div className="grid gap-1.5">
                <Label>Rapporto con le famiglie</Label>
                <Input value={form.rapporto_famiglie} onChange={(e) => set("rapporto_famiglie", e.target.value)} />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={!!form.focus_crescita} onCheckedChange={(v) => set("focus_crescita", v)} />
                  Focus crescita/formazione
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={!!form.eventi_interni} onCheckedChange={(v) => set("eventi_interni", v)} />
                  Eventi interni
                </label>
              </div>
            </>
          )}

          <div className="grid gap-1.5">
            <Label>Note</Label>
            <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} placeholder="Annotazioni, prossimi passi..." />
          </div>

          {initial?.id && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3" data-testid="ssd-eventi-section">
              {!form.email && <p className="text-[11px] text-amber-600 mb-2">Aggiungi l'email {isPartner ? "del partner" : isRitr ? "del cliente" : "della società"} per poterlo invitare.</p>}
              <AttivitaPanel fixedSsd={{ id: initial.id, tipo: "ssd", nome: form.nome, email: form.email }} canInvite />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {isTitolare && !isRitr && initial?.id && isAcq(initial, settings) && (
            initial.portfolio_id
              ? <span data-testid="ssd-in-portfolio-label" className="sm:mr-auto self-center text-xs font-semibold text-emerald-600 inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> Già in Portfolio</span>
              : <Button data-testid="ssd-to-portfolio-button" variant="ghost" className="sm:mr-auto gap-1.5 text-primary hover:text-primary" onClick={async () => {
                  if (!window.confirm(`Spostare "${initial.nome}" nel tuo Portfolio? Il collaboratore mantiene il compenso già maturato.`)) return;
                  try { await api.ssdToPortfolio(initial.id); await refresh(); toast.success("SSD aggiunta al Portfolio"); onOpenChange(false); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                }}><Briefcase className="h-4 w-4" /> Sposta in Portfolio</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="ssd-modal-submit-button" onClick={submit}>Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
